// SkuTemplatesCard — list + management of shared 品番ルール.
// iter_135 Phase 3: rules now own their segments + preset values directly
// (family model removed). Create/edit uses SkuFamilyBuilderDialog as the
// single-dialog editor (segments defined in the wizard steps; preset values
// / department scope stay on the parent rule doc via `initial`).
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Bookmark, Plus, PencilSimple, Trash, MagnifyingGlass, LockSimple, Copy, Sparkle,
  CaretDown, CaretRight, Robot, ArrowsClockwise,
} from '@phosphor-icons/react';
import SkuFamilyBuilderDialog from './SkuFamilyBuilderDialog';
import SkuChatDrawer from './SkuChatDrawer';
import { skuMajorTitle } from '@/lib/skuDisplay';
import { useLedgerCounters, ledgerLockFor } from '@/lib/ledgerPrefixes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SkuTemplatesCard({ initialFilter = null, onFilterConsumed }) {
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // template being edited, or null for new
  const [chatOpen, setChatOpen] = useState(false);
  // iter_143c · Live sample refresh — { [templateId]: { sku, loading } }.
  // Populated on-demand when the user clicks the small refresh icon next to
  // the "例: XXX" chip, so admins can verify what the next-issued SKU would
  // look like right now (accounts for counter advances, year rollover, etc.).
  const [livePreviews, setLivePreviews] = useState({});
  const ledger = useLedgerCounters();

  const refreshSample = useCallback(async (tpl) => {
    setLivePreviews((prev) => ({ ...prev, [tpl.id]: { ...(prev[tpl.id] || {}), loading: true } }));
    try {
      const r = await axios.post(
        `${API}/sku/preview`,
        { template_id: tpl.id, values: {} },
        { withCredentials: true },
      );
      setLivePreviews((prev) => ({ ...prev, [tpl.id]: { sku: r.data.sku, loading: false, at: Date.now() } }));
    } catch (e) {
      setLivePreviews((prev) => ({ ...prev, [tpl.id]: { ...(prev[tpl.id] || {}), loading: false, error: true } }));
      toast.error(e?.response?.data?.detail || 'プレビュー取得に失敗しました');
    }
  }, []);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/sku/templates`, { withCredentials: true });
    setTemplates(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // iter_150 · Drill-down from カウンター tab — pre-apply the bucket's template code.
  useEffect(() => {
    if (initialFilter) {
      setQ(initialFilter);
      onFilterConsumed?.();
    }
  }, [initialFilter, onFilterConsumed]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return templates.filter((t) => {
      if (!query) return true;
      return [t.code, t.name, t.category, t.description, skuMajorTitle(t)].some((v) => (v || '').toLowerCase().includes(query));
    });
  }, [templates, q]);

  // iter_144d · Grouping is now by rule NAME per user request "the rule name
  // IS the category, so same name should be same, like you categorised it by
  // app earlier" (option c: allow duplicate names AND group them together).
  //   • Name-groups with >=2 members render as a collapsible group.
  //   • Single-member name-groups render as flat cards (unchanged UX for the
  //     common case where names are unique).
  //   • The `category` field is preserved on the doc for back-compat but no
  //     longer used for grouping in this card.
  // 20260801 · Grouping by rule NAME, headers always shown (even for a single
  // rule) because the card's main title is now the 大分類, not the name.
  const groups = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const key = (t.name || t.code || '').trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    const arr = [...map.entries()].map(([cat, items]) => ({
      cat,
      items,
      grouped: true,
    }));
    arr.sort((a, b) => a.cat.localeCompare(b.cat, 'ja'));
    return arr;
  }, [filtered]);

  const toggleGroup = (cat) => setCollapsed((prev) => {
    const s = new Set(prev);
    if (s.has(cat)) s.delete(cat); else s.add(cat);
    return s;
  });

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (t) => {
    // 台帳が採番するルールを編集するときは、何が変わって何が変わらないかを先に伝える。
    const lock = ledgerLockFor(t, ledger);
    if (lock) {
      toast.warning(`${lock.row.owner_label} が採番するルールです`, {
        description: `形（並び・桁数）は直せますが、連番は ${lock.row.owner_screen} の`
          + 'カウンターから出ます。保存すると監査ログに残ります。',
        duration: 12000,
      });
    }
    setEditing(t);
    setEditorOpen(true);
  };

  const remove = async (t) => {
    if (!window.confirm(`品番ルール「${t.name}」を削除しますか？`)) return;
    try {
      await axios.delete(`${API}/sku/templates/${t.id}`, { withCredentials: true });
      toast.success('削除しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  const duplicate = async (t) => {
    const newCode = `${t.code}_copy`;
    try {
      const { id: _id, created_at: _ca, updated_at: _ua, created_by: _cb, ...payload } = t;
      payload.code = newCode;
      payload.name = `${t.name} (コピー)`;
      await axios.post(`${API}/sku/templates`, payload, { withCredentials: true });
      toast.success(`「${newCode}」として複製しました`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '複製に失敗しました');
    }
  };

  const isEmpty = templates.length === 0;

  return (
    <Card className="p-4" data-testid="sku-templates-card">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-zinc-700 flex items-center gap-1">
            <Bookmark weight="duotone" className="text-amber-500" />
            品番ルール ({filtered.length}/{templates.length})
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            品番の形とプリセット値をひとつのルールで管理。部門ごとに共有可能。管理者・マネージャが作成できます。
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="検索…" className="pl-7 h-8 w-40" data-testid="sku-tpl-search" />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setChatOpen(true)}
            className="border-zinc-200 text-zinc-700 hover:bg-zinc-50"
            data-testid="sku-tpl-ai-btn"
            title="AIアシスタントで品番ルールを作成・編集"
          >
            <Robot size={14} weight="fill" /><span className="ml-1">AIで作成</span>
          </Button>
          <Button size="sm" onClick={openNew} data-testid="sku-tpl-new">
            <Plus size={14} /><span className="ml-1">新規</span>
          </Button>
        </div>
      </div>

      {isEmpty ? (
        <div className="rounded-xl bg-zinc-50 border-2 border-dashed border-amber-200 p-8 text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center">
            <Sparkle weight="fill" size={22} className="text-amber-500" />
          </div>
          <div>
            <div className="text-base font-bold text-zinc-800">
              品番ルールを作ると <span className="text-amber-700">1タップで発番</span> できます
            </div>
            <p className="text-xs text-zinc-500 mt-1.5 max-w-md mx-auto leading-relaxed">
              よく使う品番の形とプリセット値を保存しておくと、次からは 品番自動採番 (#) を開いてワンタップで発行できるようになります。
            </p>
          </div>
          <Button onClick={openNew} className="bg-amber-600 hover:bg-amber-700" data-testid="sku-tpl-empty-cta">
            <Plus size={14} className="mr-1" /> 最初の品番ルールを作成
          </Button>
          <div className="pt-2 text-[11px] text-zinc-400">
            例: 「電熱課の消耗品」「製造ネジ (M4/M5)」「配線材 (赤/黒)」など
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-zinc-400 text-sm">該当する品番ルールがありません</div>
      ) : (
        <div className="space-y-3">
          {(() => {
            const catGroups = groups.filter((g) => g.grouped);
            const flatItems = groups.filter((g) => !g.grouped).flatMap((g) => g.items);
            const showExpandControls = catGroups.length >= 2;
            // 20260610 · 台帳が自前カウンターで採番するプレフィックス（正本は
            // backend/ledger_counters.py → GET /api/sku/ledger-counters）のルールは
            // 発番系の操作（サンプル発番プレビュー）を無効化し、行をグレー表示にする。
            // 行自体は表示のままで、管理者の編集・複製・削除は妨げない。
            const renderCard = (t) => {
              const lock = ledgerLockFor(t, ledger);
              const ledgerOwned = !!lock;
              return (
              <div key={t.id} className={`rounded-lg border p-3 transition-colors ${ledgerOwned ? 'border-zinc-200 bg-zinc-50' : 'border-zinc-200 bg-white hover:border-amber-300'}`} data-testid={`sku-tpl-card-${t.code}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className={`min-w-0 flex-1 ${ledgerOwned ? 'opacity-60' : ''}`}>
                    <div className="text-sm font-bold text-zinc-800 truncate">{skuMajorTitle(t)}</div>
                    {ledgerOwned && (
                      <div
                        className="mt-1 rounded border border-zinc-200 bg-white px-2 py-1.5 text-[10px] leading-relaxed text-zinc-600"
                        data-testid={`sku-tpl-ledger-owned-${t.code}`}
                      >
                        <div className="font-semibold text-zinc-700">
                          この番号は <span data-no-translate>{lock.row.owner_label}</span> が採番します
                          <span className="ml-1 font-mono text-[9px] text-zinc-400" data-no-translate>
                            {lock.row.counter}
                          </span>
                        </div>
                        <div className="mt-0.5">
                          連番を変える場所: <b>{lock.row.owner_screen}</b>
                          （このルールからは発番しません）
                        </div>
                        <div className="mt-0.5 text-zinc-500">
                          形（並び・桁数）はこのルールの
                          <b>編集</b>
                          で直せます。保存すると監査ログに残ります。
                        </div>
                      </div>
                    )}
                    <div className="text-[11px] font-mono text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span>{t.code}</span>
                      <span className="text-zinc-300">·</span>
                      <span>{t.length}桁</span>
                      {(t.sample || livePreviews[t.id]?.sku) && (
                        <>
                          <span className="text-zinc-300">·</span>
                          <span className="text-blue-600 flex items-center gap-1" title="サンプル（最新値を再取得できます）">
                            <span data-testid={`sku-tpl-sample-${t.code}`}>
                              例: {livePreviews[t.id]?.sku || t.sample}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); refreshSample(t); }}
                              disabled={livePreviews[t.id]?.loading || ledgerOwned}
                              title={ledgerOwned ? lock.note : '最新のサンプルを取得'}
                              className="inline-flex items-center justify-center h-4 w-4 rounded hover:bg-blue-50 text-blue-500 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              data-testid={`sku-tpl-refresh-${t.code}`}
                            >
                              <ArrowsClockwise
                                size={10}
                                className={livePreviews[t.id]?.loading ? 'animate-spin' : ''}
                              />
                            </button>
                            {livePreviews[t.id]?.at && !livePreviews[t.id]?.loading && (
                              <span className="text-[9px] text-emerald-600" title="ライブ値">•</span>
                            )}
                          </span>
                        </>
                      )}
                      {(t.locked_keys?.length || 0) > 0 && (
                        <>
                          <span className="text-zinc-300">·</span>
                          <span className="text-amber-600 flex items-center gap-0.5" title={`ロック: ${(t.locked_keys || []).join(', ')}`}>
                            <LockSimple size={9} weight="fill" /> {(t.locked_keys || []).length}
                          </span>
                        </>
                      )}
                      {/* 種別 — この番号が 設備(資産) か 消耗品 かの宣言。IT資産や備品の
                          登録フォームがこの値で動きを変えるので、一覧から見えるようにする。 */}
                      <span className="text-zinc-300">·</span>
                      <span
                        className={`rounded px-1 py-0.5 text-[9px] font-semibold ${
                          t.asset_kind === 'durable' ? 'bg-sky-50 text-sky-700'
                            : t.asset_kind === 'consumable' ? 'bg-purple-50 text-purple-700'
                              : 'bg-amber-50 text-amber-700'
                        }`}
                        title="この品番ルールで発番した番号の種別（編集 → 種別 で変更）"
                        data-testid={`sku-tpl-asset-kind-${t.code}`}
                      >
                        {t.asset_kind === 'durable' ? '資産（設備）'
                          : t.asset_kind === 'consumable' ? '消耗品' : '種別未指定'}
                      </span>
                    </div>
                    {t.description && (
                      <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{t.description}</div>
                    )}
                  </div>
                  <div className="flex gap-0.5 shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => duplicate(t)} title="複製" data-testid={`sku-tpl-dup-${t.code}`}>
                      <Copy size={13} className="text-blue-500" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(t)} title="編集" data-testid={`sku-tpl-edit-${t.code}`}>
                      <PencilSimple size={13} className="text-zinc-500" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-rose-50" onClick={() => remove(t)} title="削除" data-testid={`sku-tpl-del-${t.code}`}>
                      <Trash size={13} className="text-rose-500" />
                    </Button>
                  </div>
                </div>
              </div>
              );
            };
            return (
              <>
                {showExpandControls && (
                  <div className="flex justify-end gap-2 text-[11px] text-blue-600">
                    <button className="hover:underline" onClick={() => setCollapsed(new Set())} data-testid="sku-tpl-expand-all">すべて展開</button>
                    <span className="text-zinc-300">|</span>
                    <button className="hover:underline" onClick={() => setCollapsed(new Set(catGroups.map((g) => g.cat)))} data-testid="sku-tpl-collapse-all">すべて畳む</button>
                  </div>
                )}
                {/* Uncategorised (flat) — per user request "the rule IS the category". */}
                {flatItems.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="sku-tpl-flat-list">
                    {flatItems.map(renderCard)}
                  </div>
                )}
                {/* Real categories still grouped as before. */}
                {catGroups.map((g) => {
                  const isCollapsed = collapsed.has(g.cat);
                  return (
                    <div key={g.cat} className="rounded-xl border border-zinc-200 bg-white" data-testid={`sku-tpl-group-${g.cat}`}>
                      <button
                        onClick={() => toggleGroup(g.cat)}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 rounded-t-xl text-left"
                        data-testid={`sku-tpl-group-header-${g.cat}`}
                      >
                        {isCollapsed
                          ? <CaretRight size={12} weight="bold" className="text-zinc-500" />
                          : <CaretDown size={12} weight="bold" className="text-zinc-500" />}
                        <span className="text-sm font-semibold text-zinc-700">{g.cat}</span>
                        <Badge variant="outline" className="ml-1 bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">{g.items.length}</Badge>
                      </button>
                      {!isCollapsed && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-3 pb-3">
                          {g.items.map(renderCard)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>
      )}

      {/* iter_135 Phase 3 · single-dialog rule editor (segments + core fields).
          Preset values / locked keys inherit from the existing template on edit
          — they can be tweaked via a follow-up smaller edit form (backlog). */}
      <SkuFamilyBuilderDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editing}
        onSaved={() => load()}
      />
      <SkuChatDrawer
        open={chatOpen}
        onOpenChange={setChatOpen}
        onTemplatesChanged={() => load()}
      />
    </Card>
  );
}
